/**
 * VFW motion engine — the site's ONLY entrance-animation system
 * (design-system §8). No libraries. IntersectionObserver + CSS classes.
 *
 * Contract:
 * - Arms only when motion is allowed: html gets `vfw-motion-ready`,
 *   and ONLY then does CSS apply hidden pre-reveal states. No JS or
 *   `prefers-reduced-motion: reduce` → content is simply visible.
 * - Elements already in the viewport at arm time are revealed
 *   synchronously (no first-paint flash).
 * - Reveals fire once (~86% viewport line), then unobserve.
 * - Theme-editor safe: re-scans on shopify:section:load.
 * - <vfw-count> animates aggregate proof numbers (1.4s, width
 *   reserved via tabular-nums CSS); reduced motion = final value.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SELECTOR = '[data-vfw-reveal], [data-vfw-reveal-stagger]';
  var observer = null;

  function reveal(el) {
    el.classList.add('is-visible');
  }

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  }

  function scan(scope) {
    if (!observer) return;
    var targets = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      if (el.classList.contains('is-visible') || el.hasAttribute('data-vfw-observed')) continue;
      el.setAttribute('data-vfw-observed', '');
      if (inViewport(el)) {
        reveal(el);
      } else {
        observer.observe(el);
      }
    }
  }

  function disarm() {
    document.documentElement.classList.remove('vfw-motion-ready');
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    var targets = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < targets.length; i++) reveal(targets[i]);
  }

  function arm() {
    if (REDUCED.matches || observer) return;
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            reveal(entries[i].target);
            observer.unobserve(entries[i].target);
          }
        }
      },
      /* POSITIVE bottom margin: start the fade ~30% BELOW the fold so
         content has finished arriving by the time the eye reaches it.
         The old -14% margin meant a section had to be ~114px INSIDE
         the viewport before its 450ms fade even started — at reading
         scroll speed the page below the fold read as blank ivory
         (James, 2026-07-29: "sections look wrong / unresponsive"). */
      { rootMargin: '0px 0px 30% 0px', threshold: 0 }
    );
    // Mark in-view elements BEFORE the ready class lands: zero flash.
    var targets = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      el.setAttribute('data-vfw-observed', '');
      if (inViewport(el)) {
        reveal(el);
      } else {
        observer.observe(el);
      }
    }
    document.documentElement.classList.add('vfw-motion-ready');
  }

  function init() {
    if (REDUCED.matches) return;
    arm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Live preference change: honor immediately, both directions.
  var onPreferenceChange = function () {
    if (REDUCED.matches) {
      disarm();
    } else {
      arm();
      scan(document);
    }
  };
  if (typeof REDUCED.addEventListener === 'function') {
    REDUCED.addEventListener('change', onPreferenceChange);
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target || document);
    progressScan();
  });

  /* ── Scroll-progress driver (design-system §8) ──────────────
     [data-vfw-progress] elements receive --vfw-p: 0→1 as they
     traverse the viewport (0 = top edge below the fold, 1 = bottom
     edge above the top). CSS owns every visual consequence — the
     property is a unitless data channel, never a style. Scroll
     listeners ride BOTH window and .page-wrapper (Horizon 4.x:
     desktop scrolls the wrapper, mobile scrolls the window).
     Reduced motion: never armed — CSS fallback centers sprites. */
  var progressEls = null;
  var progressTicking = false;

  function progressUpdate() {
    progressTicking = false;
    if (!progressEls || REDUCED.matches) return;
    var vh = window.innerHeight;
    for (var i = 0; i < progressEls.length; i++) {
      var r = progressEls[i].getBoundingClientRect();
      // No proximity guard: every element always carries a correct,
      // continuous value (0 below fold, 1 above) — the sprite never
      // pops from its CSS resting default on first approach.
      var p = (vh - r.top) / (vh + r.height);
      if (p < 0) p = 0;
      else if (p > 1) p = 1;
      progressEls[i].style.setProperty('--vfw-p', p.toFixed(4));
    }
  }

  function progressRequest() {
    if (progressTicking) return;
    progressTicking = true;
    requestAnimationFrame(progressUpdate);
  }

  function progressScan() {
    if (REDUCED.matches) return;
    var found = document.querySelectorAll('[data-vfw-progress]');
    if (!found.length) {
      progressEls = null;
      return;
    }
    var attach = progressEls === null;
    progressEls = found;
    if (attach) {
      // Capture-phase document listener hears EVERY scroll container
      // (scroll doesn't bubble; 4.x splits scrolling between window
      // and .page-wrapper by breakpoint — this covers both and any
      // future scroller).
      document.addEventListener('scroll', progressRequest, { passive: true, capture: true });
      window.addEventListener('resize', progressRequest, { passive: true });
    }
    progressRequest();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', progressScan, { once: true });
  } else {
    progressScan();
  }

  /* ── <vfw-count> — aggregate proof numbers only (§8) ────── */
  var DURATION = 1400;

  var VfwCount = function () {
    return Reflect.construct(HTMLElement, [], VfwCount);
  };
  VfwCount.prototype = Object.create(HTMLElement.prototype, {
    connectedCallback: {
      value: function () {
        var el = this;
        var target = parseFloat(el.getAttribute('data-value') || el.textContent.replace(/[^0-9.]/g, ''));
        if (!isFinite(target)) return;
        var prefix = el.getAttribute('data-prefix') || '';
        var suffix = el.getAttribute('data-suffix') || '';
        var format = function (n) {
          return prefix + Math.round(n).toLocaleString('en-US') + suffix;
        };
        el.textContent = format(target); // reserve width at final value
        if (REDUCED.matches || typeof IntersectionObserver === 'undefined') return;

        var played = false;
        var io = new IntersectionObserver(
          function (entries) {
            if (played || !entries.some(function (x) { return x.isIntersecting; })) return;
            played = true;
            io.disconnect();
            var start = null;
            var ease = function (t) { return 1 - Math.pow(1 - t, 3); };
            var frame = function (ts) {
              if (start === null) start = ts;
              var p = Math.min((ts - start) / DURATION, 1);
              el.textContent = format(target * ease(p));
              if (p < 1) requestAnimationFrame(frame);
            };
            requestAnimationFrame(frame);
          },
          { threshold: 0.6 }
        );
        io.observe(el);
      },
    },
  });
  Object.setPrototypeOf(VfwCount, HTMLElement);
  if (typeof customElements !== 'undefined' && !customElements.get('vfw-count')) {
    customElements.define('vfw-count', VfwCount);
  }
})();
